import { Button, makeStyles, Card, Typography, Box } from "@material-ui/core"
import React, { useState } from "react"
import { Link } from "react-router-dom"
import stepCardConstant from "../../constants/stepCard.constant"
import { useDialogContext } from "../../providers/dialog.provider"
import { useApplicationStateContext } from "../../providers/applicationState.provider"
import { dateToLocaleString, dateToInterviewRound, resolveRegistrationTime } from "../../../utils/conversion"
import { useAuthContext } from "../../providers/auth.provider"
import MyProfileModel from "../../models/myprofile.models"
import copyToClipBoard from "../../../assets/images/icon/copy-to-clipboard.svg"
import { useGlobalContext } from "../../providers/global.provider"
import ApplicationServiceAPI from "../../services/application.service"

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(4),
    boxShadow: "0px 100px 257px rgba(0, 0, 0, 0.07), 0px 18.576px 34.4894px rgba(0, 0, 0, 0.0269069)",
    borderRadius: "10px",
    margin: theme.spacing(2.25)
  },
  stepNo: {
    borderRadius: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    width: 56,
    height: 56,
    margin: theme.spacing(1)
  },
  icon: {
    height: 22,
    verticalAlign: "sub"
  },
  redCircle: {
    background: theme.palette.primary.main,
    color: "#FFFFFF" //theme.palette.gray[0]
  },
  outlinedRedCircle: {
    border: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main //theme.palette.gray[0]
  },
  outlinedGrayCircle: {
    border: "3px solid #BFBFBF", //theme.palette.gray[200]
    color: "#BFBFBF" //theme.palette.gray[200]
  },
  stepIndicator: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "150px",
    marginRight: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      marginRight: theme.spacing(0),
      marginBottom: theme.spacing(2)
    }
  },
  text: {
    textAlign: "left",
    maxWidth: "520px"
  },
  title: {
    fontWeight: 500,
    textTransform: "uppercase",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      marginBottom: theme.spacing(2)
    }
  },
  blackText: {
    color: "#262626" //theme.palette.gray[800]
  },
  redText: {
    color: theme.palette.primary.main
  },
  grayText: {
    color: "#BFBFBF" //theme.palette.gray[200]
  },
  boldText: {
    fontWeight: 500
  },
  content: {
    color: "#8C8C8C" //theme.palette.gray[300]
  },
  button: {
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: "10px",
    flexGrow: 1
  },
  linkButton: {
    display: "flex",
    flexGrow: 1,
    "&::after": {
      content: "none"
    }
  },
  paragraphTop: {
    marginBottom: theme.spacing(1.5)
  },
  link: {
    color: theme.palette.primary.main,
    "&::after": {
      content: "none"
    }
  },
  solid: {
    color: "white",
    background: theme.palette.primary.main,
    "&:hover": {
      background: theme.palette.primary.dark
    }
  },
  outlined: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main
  },
  buttonContainer: {
    display: "flex",
    columnGap: theme.spacing(2),
    flexWrap: "wrap",
    maxWidth: "520px",
    flexGrow: 1
  },
  flexRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "left",
    flexGrow: 1,
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      justifyContent: "center"
    }
  },
  flexCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "left"
  }
}))

export interface StepCardProps {
  step: 1 | 2 | 3 | 4 | 5 | 6 | 7
  status: "complete" | "inProgress" | "incomplete"
  isApproved: "true" | "false"
}

interface InterviewStepCardProps {
  status: "complete" | "inProgress" | "incomplete"
  interviewType: string
  interviewTime: string | undefined
  interviewRoom: string | undefined
}

const StepCard: React.FC<StepCardProps> = ({ step, status, isApproved }) => {
  const classes = useStyles()
  const { openPaymentDialog, openShirtSizeDialog } = useDialogContext()
  const { me } = useAuthContext()
  const { application, mutateApplication } = useApplicationStateContext()
  const { documentState } = me.data as MyProfileModel
  const { activeSnackBar } = useGlobalContext()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const text = stepCardConstant[step][status][isApproved]!
  const zoomName = application.lgNumber.trim() + "_น้อง" + application.nickName.trim() + "_" + application.firstName.trim()

  const setStepIndicatorStyles = (status: String) => {
    switch (status) {
      case "complete":
        return [classes.redCircle, classes.blackText]
      case "inProgress":
        return [classes.outlinedRedCircle, classes.redText]
      case "incomplete":
        return [classes.outlinedGrayCircle, classes.grayText]
      default:
        return []
    }
  }

  const resolveButton = (isPrimary: boolean, onClick?: () => void) => {
    return (
      <Button
        variant={`${isPrimary ? "contained" : "outlined"}`}
        disableElevation
        className={`${classes.button} ${isPrimary ? classes.solid : classes.outlined}`}
        onClick={onClick}>
        {isPrimary ? text.primaryButton!.label : text.secondaryButton!.label}
      </Button>
    )
  }

  const copyZoomNameToClipboard = async () => {
    if ("clipboard" in navigator) return await navigator.clipboard.writeText(zoomName)
  }

  const CopyToClipboardIcon: React.FC<React.HTMLAttributes<HTMLImageElement>> = props => {
    return <img src={copyToClipBoard} onClick={copyZoomNameToClipboard} alt="" style={{ cursor: "pointer" }} {...props} />
  }

  const renderButton = (opensDialog: boolean, dialogType: string | undefined, isPrimary: boolean, isExternalPath: boolean | undefined) => {
    if (opensDialog) {
      if (dialogType === "payment") return resolveButton(isPrimary, openPaymentDialog)
      if (dialogType === "shirtSize") return resolveButton(isPrimary, openShirtSizeDialog)
    }
    if (isExternalPath)
      return (
        <a
          href={`${isPrimary ? text.primaryButton!.path : text.secondaryButton!.path}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`no-underline ${classes.linkButton}`}>
          {resolveButton(isPrimary)}
        </a>
      )
    else
      return (
        <Link to={`${isPrimary ? text.primaryButton!.path : text.secondaryButton!.path}`} className={`no-underline ${classes.linkButton}`}>
          {resolveButton(isPrimary)}
        </Link>
      )
  }

  const InterviewStepCard: React.FC<InterviewStepCardProps> = ({ status, interviewType, interviewTime, interviewRoom }) => {
    if (status === "inProgress") {
      if (!interviewTime) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Typography variant="subtitle2" className={`${classes.text} ${classes.content}`}>
              <>
                ขอแสดงความยินดี !! น้องเป็นหนึ่งในผู้มีสิทธิ์สัมภาษณ์ เตรียมตัวได้พบปะพูดคุยกับพี่ ๆ ผู้สัมภาษณ์สุดน่ารักและใจดีในวันที่ 18 ตุลาคม
                2568 ณ คณะวิศวกรรมศาสตร์ จุฬาลงกรณ์มหาวิทยาลัย สำหรับน้อง ๆ กรุงเทพฯ และปริมณฑล หรือ สัมภาษณ์ผ่านทางซูมสำหรับน้อง ๆ ต่างจังหวัด ได้เลย
                !!
              </>
            </Typography>
            <div>
              <a href="https://forms.gle/NNDrYKesvzRBKyiT9" target="_blank" rel="noopener noreferrer" style={{ color: "#941014" }}>
                รบกวนน้อง ๆ กรอกแบบฟอร์ม PDPA ก่อนมาสัมภาษณ์ด้วยน้าา
              </a>
            </div>
            <a
              href="/assets/file/อัปเดต -รายชื่อผู้มีสิทธิ์สัมภาษณ์ ค่ายลานเกียร์ 25.pdf"
              target="_blank"
              download
              rel="noopener noreferrer"
              style={{ color: "blue", textDecoration: "underline" }}>
              ตรวจสอบรอบการสัมภาษณ์ที่นี่ (อัปเดตวันที่ 10 ตุลาคม 2568)
            </a>
            {/*<div className={classes.buttonContainer}>
              {text.secondaryButton &&
                renderButton(text.secondaryButton.opensDialog, text.secondaryButton.dialogType, false, text.secondaryButton.isExternalPath)}
            </div>*/}
          </div>
        )
      } else {
        const date = new Date(application.interviewTime!).getDate()
        return (
          <>
            {interviewType === "online" && (
              <>
                <Typography variant="subtitle2" className={`${classes.text} ${classes.content} ${classes.paragraphTop}`}>
                  รอบสัมภาษณ์ของน้องจะเป็น <span className={`${classes.redText} ${classes.boldText}`}>{dateToLocaleString(interviewTime)}</span>
                </Typography>
                <Typography variant="subtitle2" className={`${classes.text} ${classes.content}`}>
                  {text.contents}
                  รบกวนน้องตั้งชื่อใน ZOOM ว่า "{zoomName}" นะครับ <CopyToClipboardIcon className={classes.icon} />
                </Typography>

                <div className={classes.buttonContainer}>
                  {text.primaryButton &&
                    date === 18 &&
                    renderButton(text.primaryButton.opensDialog, text.primaryButton.dialogType, true, text.primaryButton.isExternalPath)}
                  {text.secondaryButton &&
                    date === 21 &&
                    renderButton(text.secondaryButton.opensDialog, text.secondaryButton.dialogType, false, text.secondaryButton.isExternalPath)}
                </div>
              </>
            )}
            {interviewType === "onsite" && (
              <>
                <Typography variant="subtitle2" className={`${classes.text} ${classes.content} ${classes.paragraphTop}`}>
                  การสัมภาษณ์จะเป็นในรูปแบบออนไซต์ ที่คณะวิศวกรรมศาสตร์ จุฬาลงกรณ์มหาวิทยาลัย{" "}
                  <span className={`${classes.redText} ${classes.boldText}`}>{dateToInterviewRound(interviewTime)}</span>
                </Typography>
                <Typography variant="subtitle2" className={`${classes.text} ${classes.content} ${classes.paragraphTop}`}>
                  โดยจะเปิดให้ลงทะเบียนตั้งแต่เวลา{" "}
                  <span className={`${classes.redText} ${classes.boldText}`}>{resolveRegistrationTime(dateToInterviewRound(interviewTime))}</span>
                </Typography>
                <Typography variant="subtitle2" className={`${classes.text} ${classes.content} ${classes.paragraphTop}`}>
                  ที่บริเวณ <span className={`${classes.redText} ${classes.boldText}`}>ลานเกียร์ โต๊ะลงทะเบียน {interviewRoom}</span>
                </Typography>
                <Typography variant="subtitle2" className={`${classes.text} ${classes.content} ${classes.paragraphTop}`}>
                  น้อง ๆ สามารถติดตามรายละเอียดการสัมภาษณ์ได้ที่เพจเฟซบุ๊ก LarnGear Camp
                </Typography>
                <a
                  href="https://www.facebook.com/share/p/ZXqd8NQBHzuqx8uS/"
                  className="no-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#941014" }}>
                  Checklist สำหรับการเตรียมตัวสัมภาษณ์
                </a>
                <a
                  href="https://www.facebook.com/share/p/3nHP5xyyFKqJeUYU/"
                  className="no-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#941014" }}>
                  แผนที่การเดินทางไปคณะวิศวกรรมศาสตร์
                </a>
                <a
                  href="https://forms.gle/CP2nirG9D9MewGH47"
                  className="no-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#941014" }}>
                  แบบฟอร์มยินยอมให้เก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคล
                </a>
              </>
            )}
          </>
        )
      }
    }
    return (
      <>
        <Typography variant="subtitle2" className={`${classes.text} ${classes.content}`}>
          {text.contents}
        </Typography>
      </>
    )
  }

  const checkInterviewType = (interviewAvailability: string): string => {
    if (interviewAvailability === "AVAILABLE" || interviewAvailability === "NOT_SURE") return "onsite"
    return "online"
  }

  return (
    <Card className={`${classes.card} ${classes.flexRow}`}>
      <div className={classes.stepIndicator}>
        <Box className={`${classes.stepNo} ${setStepIndicatorStyles(status)[0]}`}>
          <Typography variant="h3">{step}</Typography>
        </Box>
        <Typography variant="subtitle1" className={`${classes.title} ${setStepIndicatorStyles(status)[1]}`}>
          {status === "inProgress" ? "in progress" : status}
        </Typography>
      </div>

      <div className={classes.flexCol}>
        <Typography variant="h4" className={`${classes.text} ${classes.title} ${classes.blackText}`}>
          {text.title}
        </Typography>
        {step === 6 && documentState.payment === "PASSED" && (
          <Typography variant="subtitle2" className={`${classes.text} ${classes.boldText} ${classes.redText} ${classes.paragraphTop}`}>
            หลักฐานการชำระเงินได้รับการอนุมัติแล้ว
          </Typography>
        )}
        {step === 6 && documentState.payment === "CHANGE_REQUIRED" && (
          <>
            <Typography variant="subtitle2" className={`${classes.text} ${classes.boldText} ${classes.redText} ${classes.paragraphTop}`}>
              หลักฐานการชำระเงินไม่ถูกต้อง
            </Typography>
            <Typography variant="subtitle2" className={`${classes.text} ${classes.boldText} ${classes.content} ${classes.paragraphTop}`}>
              จากทีมงาน : {application.payment.comment !== "" ? application.payment.comment : "โปรดอัปโหลดอีกครั้ง"}
            </Typography>
          </>
        )}
        {step === 5 && status === "inProgress" && application.studentType && (
          <div style={{ marginTop: "0.75rem", marginBottom: "0.75rem" }}>
            <Typography
              variant="subtitle2"
              className={`${classes.text} ${classes.boldText}`}
              style={{ color: application.studentType === "MAIN" ? "#39B774" : "#FFA500" }}>
              ผลการสัมภาษณ์: {application.studentType === "MAIN" ? "เป็นตัวจริง" : "เป็นตัวสำรอง"}
            </Typography>
          </div>
        )}
        {step === 4 && (
          <InterviewStepCard
            status={status}
            interviewType={checkInterviewType(application.interviewAvailability)}
            interviewTime={application.interviewTime}
            interviewRoom={application.interviewRoom}
          />
        )}
        {step !== 4 && step !== 5 && (
          <>
            <Typography variant="subtitle2" className={`${classes.text} ${classes.content}`}>
              {text.contents}
            </Typography>
            <div className={classes.buttonContainer}>
              {text.primaryButton &&
                (step !== 6 || (step === 6 && application.shirtSize)) &&
                renderButton(text.primaryButton.opensDialog, text.primaryButton.dialogType, true, text.primaryButton.isExternalPath)}
              {text.secondaryButton &&
                renderButton(text.secondaryButton.opensDialog, text.secondaryButton.dialogType, false, text.secondaryButton.isExternalPath)}
            </div>
            {step === 6 && application.payment.updatedAt && (
              <Typography variant="subtitle2" className={`${classes.text} ${classes.content}`}>
                Last uploaded at: {new Date(application.payment.updatedAt).toLocaleString()}
              </Typography>
            )}
            {step === 7 && application.state === "PASSED_THE_CAMP" && (
              <>
                {application.certificateDownloadLink && (
                  <a
                    href={`${application.certificateDownloadLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`no-underline ${classes.linkButton}`}>
                    <Button variant={"contained"} disableElevation className={`${classes.button} ${classes.solid}`} onClick={() => {}}>
                      ดาวน์โหลดเกียรติบัตร
                    </Button>
                  </a>
                )}
                {!application.certificateDownloadLink && (
                  <Typography variant="subtitle2" className={`${classes.text} ${classes.content}`} style={{ fontWeight: 500, color: "#941014" }}>
                    ขอแสดงความเสียใจ น้องไม่ได้รับเกียรติบัตรในการเข้าร่วมค่ายลานเกียร์ครั้งนี้
                  </Typography>
                )}
              </>
            )}
          </>
        )}
        {step === 5 && (
          <>
            <Typography variant="subtitle2" className={`${classes.text} ${classes.content}`}>
              {text.contents}
            </Typography>
            {status === "inProgress" && application.studentType && application.campAvailability === null && (
              <div style={{ marginTop: "1rem" }}>
                <Typography variant="subtitle2" className={`${classes.text} ${classes.boldText}`} style={{ marginBottom: "0.5rem" }}>
                  แจ้งความสะดวกในการเข้าร่วมค่าย:
                </Typography>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input type="radio" name="campAvailability" value="available" style={{ marginRight: "0.5rem" }} />
                    <span>สะดวก</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input type="radio" name="campAvailability" value="unavailable" style={{ marginRight: "0.5rem" }} />
                    <span>ไม่สะดวก</span>
                  </label>
                </div>
                <Button
                  variant="contained"
                  disableElevation
                  className={`${classes.button} ${classes.solid}`}
                  style={{ marginTop: "1rem" }}
                  disabled={isSubmitting}
                  onClick={async () => {
                    const selected = document.querySelector('input[name="campAvailability"]:checked') as HTMLInputElement
                    if (!selected) {
                      activeSnackBar({ type: "warning", message: "กรุณาเลือกคำตอบก่อนกดส่ง" })
                      return
                    }

                    setIsSubmitting(true)
                    try {
                      const campAvailability = selected.value === "available"
                      await ApplicationServiceAPI.updateApplicationPostSubmitAPI({ campAvailability })

                      // Refresh the data
                      await mutateApplication()

                      // Show success message
                      activeSnackBar({
                        type: "success",
                        message: campAvailability ? "บันทึกแล้ว: คุณสะดวกเข้าร่วมค่าย" : "บันทึกแล้ว: คุณไม่สะดวกเข้าร่วมค่าย"
                      })
                    } catch (error) {
                      console.error("Error updating availability:", error)
                      activeSnackBar({ type: "error", message: "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง" })
                    } finally {
                      setIsSubmitting(false)
                    }
                  }}>
                  {isSubmitting ? "กำลังบันทึก..." : "ส่งข้อมูล"}
                </Button>
              </div>
            )}
            {application.campAvailability !== null && application.campAvailability !== undefined && (
              <Typography
                variant="subtitle2"
                className={`${classes.text} ${classes.boldText}`}
                style={{ marginTop: "1rem", color: application.campAvailability ? "#39B774" : "#941014" }}>
                คุณได้แจ้งว่า: {application.campAvailability ? "สะดวก" : "ไม่สะดวก"}
              </Typography>
            )}
          </>
        )}
      </div>
    </Card>
  )
}

export default StepCard
